<script lang="ts">
import {
    defineComponent,
    onMounted,
    onBeforeUnmount,
    ref,
    watch,
    toRefs,
    h,
    VNode,
    computed,
    onBeforeUpdate
} from 'vue';

interface IProps {
  defaultIndex: number;
  resetTabs: boolean;
  position: string;
  direction: string;
  reverse: boolean;
}

export default defineComponent({
    // TODO: Should fix below so it doesn't clash with HTML elements
    // eslint-disable-next-line vue/multi-word-component-names
    name: 'Tabs',
    props: {
        defaultIndex: {
            default: 0,
            type: Number
        },
        resetTabs: {
            type: Boolean,
            default: false
        },
        direction: {
            type: String,
            default: 'horizontal',
            validator(value: string) {
                return [ 'horizontal', 'vertical' ].includes(value);
            }
        },
        position: {
            type: String,
            default: 'left',
            validator(value: string) {
                return [
                    'left', 'start', 'end', 'center'
                ].includes(value);
            }
        },
        reverse: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: {
        tabChanged(index: number) {
            return index !== undefined || index !== null;
        }
    },
    setup(props: IProps, { emit, slots }) {
        const { defaultIndex, resetTabs, position, direction, reverse } =
      toRefs(props);

        const selectedIndex = ref(0);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const tabs = ref<Array<any>>([]);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const _tabItems = ref<any[]>([]);

        const onTabKeyDown = (e: KeyboardEvent) => {
            if (e.ctrlKey || e.metaKey) {
                if (parseInt(e.key) - 1 in tabs.value) {
                    e.preventDefault();
                    switchTab(e, parseInt(e.key) - 1, tabs.value[parseInt(e.key) - 1]);
                }
            }
        };

        const reset = () => {
            selectedIndex.value = 0;
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const switchTab = (_: any, index: number, isDisabled: boolean) => {
            if (!isDisabled) {
                selectedIndex.value = index;
                emit('tabChanged', index);
            }
        };

        onMounted(() => {
            getTabItems();
            document.addEventListener('keydown', onTabKeyDown);
        });

        onBeforeUnmount(() => {
            document.removeEventListener('keydown', onTabKeyDown);
        });

        watch(defaultIndex, (newValue) => {
            if (newValue !== selectedIndex.value) {
                selectedIndex.value = newValue;
            }
        });

        watch(resetTabs, (newValue) => {
            if (newValue === true) reset();
        });

        onBeforeUpdate(() => {
            getTabItems();
        });

        const getTabItems = () => {
            _tabItems.value.splice(0, _tabItems.value.length);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (slots as any).default().forEach((component: any) => {
                if (component.type.name && component.type.name === 'Tab') {
                    _tabItems.value.push(component);
                } else {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    component.children.forEach((cComp: any) => {
                        if (cComp.type.name && cComp.type.name === 'Tab') {
                            _tabItems.value.push(cComp);
                        }
                    });
                }
            });
        };
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const getTitleSlotContent = (titleSlot: string): any => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let slotContent: any = null;
            let shouldSkip = false;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (slots as any).default().forEach((item: any) => {
                if (shouldSkip) {
                    return;
                }

                if (item.type === 'template' && item.props.name === titleSlot) {
                    slotContent = item.children;
                    shouldSkip = true;
                } else {
                    if (item.children.length) {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        item.children.forEach((cItem: any) => {
                            if (shouldSkip) {
                                return;
                            }
                            if (cItem.props.name === titleSlot) {
                                slotContent = cItem.children;
                                shouldSkip = true;
                            }
                        });
                    }
                }
            });
            return slotContent === null ? [] : slotContent;
        };

        const tabToDisplay = computed(() => {
            return _tabItems.value.map((item, idx) => {
                return h(
                    'div',
                    {
                        class: 'tab',
                        style: `
              visibility: ${selectedIndex.value === idx ? 'visible' : 'hidden'};
              max-height: ${selectedIndex.value === idx ? 'unset' : 0};
            `
                    },
                    item
                );
            });
            // return h("div", { class: "tab" }, _tabItems.value[selectedIndex.value]);
        });

        return () => {
            const tabList: Array<VNode> = [];
            _tabItems.value.forEach((tab: VNode, index: number) => {
                const _tabProps = tab.props as {
          title?: string;
          'title-slot'?: string;
          disabled?: boolean | string;
        };

                const titleContent = _tabProps['title-slot']
                    ? getTitleSlotContent(_tabProps['title-slot'])
                    : _tabProps.title;
                const isDisabled =
          _tabProps.disabled === true || _tabProps.disabled === '';
                tabs.value[index] = isDisabled;

                tabList.push(
                    h(
                        'li',
                        {
                            class: 'tab-list__item',
                            tabIndex: '0',
                            role: 'tabItem',
                            'aria-selected': selectedIndex.value === index ? 'true' : 'false',
                            'aria-disabled': isDisabled ? 'true' : 'false',
                            onClick: (e: MouseEvent) => {
                                switchTab(e, index, isDisabled);
                            }
                        },
                        titleContent
                    )
                );
            });

            return h(
                'div',
                {
                    class: `tabs ${direction.value} ${reverse.value ? 'reverse' : ''}`,
                    role: 'tabs'
                },
                [
                    h(
                        'ul',
                        { class: `tab-list ${position.value}`, role: 'tabList' },
                        tabList
                    ),
                    ...tabToDisplay.value
                ]
            );
        };
    }
});
</script>

<style lang="scss">
:root {
  --primary-color: #096446;
  --border-color: #e2e2e2;
  --disabled-text-color: #999;
}
.tabs {
  display: grid;
  grid-template-columns: 1fr;
  .tab-list {
    list-style: none;
    display: flex;
    padding-left: 0;
    // border-bottom: 1px solid var(--border-color);
    &.center {
      justify-content: center;
    }
    &.end {
      justify-content: flex-end;
    }
    &__item {
      padding: 8px 10px;
      cursor: pointer;
      user-select: none;
      transition: border 0.3s ease-in-out;
      position: relative;
      bottom: -1px;
      // text-transform: uppercase;
      font-size: 1rem;
      // letter-spacing: 0.05rem;
      font-weight: 700;
      color: #535859;

      &:not(:first-child) {
        margin-left: 10px;
      }

      &[aria-selected="true"] {
        border-bottom: 4px solid var(--primary-color);
        color: var(--primary-color);
      }
      &[aria-disabled="true"] {
        cursor: not-allowed;
        color: var(--disabled-text-color);
      }
    }
  }
  &.horizontal {
    &.reverse {
      .tab-list {
        grid-row: 2;
        border: none;
        border-top: 1px solid var(--border-color);
      }
    }
  }

  &.vertical {
    grid-template-columns: auto 1fr;
    gap: 1rem;
    .tab-list {
      flex-direction: column;
      border-bottom: none;
      border-right: 1px solid var(--border-color);

      &__item {
        margin-left: 0;
        border-radius: 0;

        &[aria-selected="true"] {
          border: none;
          border-left: 2px solid var(--primary-color);
        }
      }
    }

    &.reverse {
      grid-template-columns: 1fr auto;
      .tab-list {
        grid-column: 2;
        border: none;
        border-left: 1px solid var(--border-color);
      }

      .tab {
        grid-row: 1;
        grid-column: 1;
      }
    }
  }
}
</style>
